import { type KeyboardEvent, useCallback, useEffect, useState } from 'react'

export const useExpandableHeroVideo = () => {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const resumeIfPaused = useCallback(() => {
    if (video?.paused) {
      video.play().catch(() => undefined)
    }
  }, [video])

  useEffect(() => {
    const onFullscreenChange = () => {
      const isVideoFullscreen = document.fullscreenElement === video
      setIsFullscreen(isVideoFullscreen)
      if (!isVideoFullscreen) {
        resumeIfPaused()
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [video, resumeIfPaused])

  useEffect(() => {
    if (!video) return
    // iOS Safari's native video fullscreen fires this on the video element
    // itself instead of `fullscreenchange` on the document. Bound off state
    // (not a plain ref) so it re-binds if the video node is attached on a
    // later render - `FeatureShowcase` is a single persistent instance
    // reused across Discover more tabs, so the expandable tab's video may
    // not be the one present on first mount.
    video.addEventListener('webkitendfullscreen', resumeIfPaused)
    return () =>
      video.removeEventListener('webkitendfullscreen', resumeIfPaused)
  }, [video, resumeIfPaused])

  const openFullscreen = useCallback(() => {
    if (!video || document.fullscreenElement) return
    const enterNativeVideoFullscreen = () => {
      // iOS Safari exposes only the video-specific API
      ;(
        video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }
      ).webkitEnterFullscreen?.()
    }
    // iOS Safari reports document.fullscreenEnabled as false even though
    // requestFullscreen exists on the element; calling the webkit fallback
    // synchronously (rather than from the rejected promise) keeps it inside
    // the user-gesture window it requires.
    if (document.fullscreenEnabled && video.requestFullscreen) {
      video.requestFullscreen().catch(enterNativeVideoFullscreen)
    } else {
      enterNativeVideoFullscreen()
    }
  }, [video])

  const onKeyDown = (e: KeyboardEvent<HTMLVideoElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openFullscreen()
    }
  }

  return { videoRef: setVideo, isFullscreen, openFullscreen, onKeyDown }
}
